import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  DollarSign,
  Calendar,
  Building2,
  Eye,
  Edit,
  Download,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  negotiating: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

export const Quotes = () => {const { t } = useTranslation("crm");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewQuote, setShowNewQuote] = useState(false);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/quotes`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch quotes');
      const data = await response.json();
      setQuotes(data);
    } catch (err) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [activeTab]);

  const filteredQuotes = quotes.filter((quote) => {
    if (!searchTerm) return true;
    return quote.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.opportunity_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: quotes.length,
    draft: quotes.filter((q) => q.status === 'draft').length,
    sent: quotes.filter((q) => q.status === 'sent' || q.status === 'negotiating').length,
    accepted: quotes.filter((q) => q.status === 'accepted').length,
    totalValue: quotes.reduce((sum, q) => sum + (q.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-quotes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("quotes", "Quotes")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageSalesQuotes", "Manage sales quotes and proposals")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchQuotes} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowNewQuote(true)}>
            <Plus className="h-4 w-4" />{t("newQuote", "New Quote")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-5">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <FileText className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalQuotes", "Total Quotes")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-gray-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-500/10">
                <Clock className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("drafts", "Drafts")}</p>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-2xl font-bold">{stats.sent}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("accepted", "Accepted")}</p>
                <p className="text-2xl font-bold">{stats.accepted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalValue", "Total Value")}</p>
                <p className="text-2xl font-bold">${(stats.totalValue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="draft">{t("draft", "Draft")}</TabsTrigger>
                <TabsTrigger value="sent">{t("sent", "Sent")}</TabsTrigger>
                <TabsTrigger value="negotiating">{t("negotiating", "Negotiating")}</TabsTrigger>
                <TabsTrigger value="accepted">{t("accepted", "Accepted")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("quoteId", "Quote ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("opportunity", "Opportunity")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("total", "Total")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("discount", "Discount")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("validUntil", "Valid Until")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) =>
                <tr key={quote.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{quote.id}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium">{quote.opportunity_name}</p>
                        <p className="text-xs text-muted-foreground">{t("id", "ID:")}{quote.opportunity_id}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{quote.company}</p>
                          <p className="text-xs text-muted-foreground">{quote.contact_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={statusColors[quote.status] || statusColors.draft}>
                        {quote.status === 'accepted' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {quote.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                        {quote.status === 'draft' && <Clock className="h-3 w-3 mr-1" />}
                        {quote.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold text-teal-600">${quote.total?.toLocaleString() || 0}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {quote.discount > 0 ?
                    <Badge variant="secondary">{quote.discount}{t("off", "% off")}</Badge> :

                    <span className="text-muted-foreground">-</span>
                    }
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Download className="h-4 w-4" />
                        </Button>
                        {quote.status === 'draft' &&
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Send className="h-4 w-4" />
                          </Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredQuotes.length === 0 &&
          <div className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noQuotesFound", "No quotes found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
      {showNewQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div role="dialog" aria-modal="true" aria-label="New Quote" className="w-full max-w-2xl rounded-lg border bg-background shadow-xl">
            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">New Quote</h2>
              <p className="text-sm text-muted-foreground">Prepare a quote with customer, opportunity, line total, discount, and validity.</p>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <Input placeholder="Quote title" />
              <Input placeholder="Opportunity" />
              <Input placeholder="Customer" />
              <Input placeholder="Total value" />
              <Input placeholder="Discount %" />
              <Input placeholder="Valid until" />
            </div>
            <div className="flex justify-end gap-2 border-t p-5">
              <Button variant="outline" onClick={() => setShowNewQuote(false)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700">Create Quote</Button>
            </div>
          </div>
        </div>
      )}
    </div>);

};
